import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  AwsMap,
  getMarkerIconForState as getMarkerIconForAws,
  MarkerState as AwsMarkerState
} from '@kyruus/aws-map';
import { DynamicGmap, resetMarkersState } from '@kyruus/gmap';
import { useMapHighlighting } from '../hooks/useMapHighlighting';
import ResizableMapSection from 'Src/search-v9/components/ResizableMapSection/ResizableMapSection';
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';
import ProviderOverlay from 'Src/search-v9/components/ProviderOverlay/ProviderOverlay';
import useMarkerPositioning from 'Src/search-v9/components/hooks/useMarkerPositioning';
import { useMapQueryParams } from '../hooks/useMapQueryParams';
import { useTheme } from '@emotion/react';
import { ZINDEXES } from '@kyruus/gmap';
import {
  getMarkerIconForState as getMarkerIconForGmaps,
  MarkerState as GmapMarkerStates
} from '@kyruus/gmap';
import {
  transformLocationsForGoogleMap,
  transformLocationsForAwsMap
} from 'Src/search-v9/utils/mapLocationTransformers';

const NORTH_LATITUDE_BOUND = 85;
const SOUTH_LATITUDE_BOUND = -85;
const EAST_LONGITUDE_BOUND = 180;
const WEST_LONGITUDE_BOUND = -180;

/**
 * ProviderMapContainer component displays a map view next to the provider list
 * when the provider_list_maps_enabled feature flag is enabled
 * Shows all provider locations as pins on the map
 *
 * Expected data structure for providers:
 * providers = [
 *   {
 *     id: string,
 *     name: string,
 *     locations: [
 *       {
 *         id: string,
 *         name: string,
 *         address: {
 *           coordinates: {
 *             lat: number,
 *             lon: number
 *           }
 *         }
 *       }
 *     ]
 *   }
 * ]
 */
function ProviderMapContainer({
  providers,
  config,
  appSettings,
  hoveredProviderId = null,
  log,
  onMarkerClick,
  getUpdatedSearch,
  location,
  match
}) {
  const markersRef = useRef(null);
  const mapRef = useRef(null);
  const awsMapRef = useRef(null);
  const mapBoundsRef = useRef(null);
  const mapContainerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [mapLocations, setMapLocations] = useState([]);
  const [locationIdsSet, setLocationIdsSet] = useState(new Set());
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [showSearchAreaButtonInMap, setShowSearchAreaButtonInMap] =
    useState(false);
  const [showProviderCard, setShowProviderCard] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const mapInitTimeoutRef = useRef(null);

  const theme = useTheme();

  const { setMarkers } = useMapHighlighting(providers, hoveredProviderId);
  const { calculateCenterRelativeToMarker, MAP_PROVIDERS } =
    useMarkerPositioning(mapRef, mapContainerRef, { duration: 300 });
  const { mapZoom, mapCenter } = useMapQueryParams({ location });

  const isAdvancedMarkersEnabled =
    config?.darkship_feature_flags?.enable_advanced_markers_for_maps;
  const isMapProviderCardEnabled =
    config?.darkship_feature_flags?.use_map_provider_card_on_marker_click;

  // Cleanup function to prevent memory leaks when component unmounts
  useEffect(() => {
    return () => {
      if (mapInitTimeoutRef.current) {
        clearTimeout(mapInitTimeoutRef.current);
        mapInitTimeoutRef.current = null;
      }
      // Clean up refs when component unmounts
      if (markersRef.current) {
        markersRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current = null;
      }
      if (mapBoundsRef.current) {
        mapBoundsRef.current = null;
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, []);

  // Scroll detection effect
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Debounced function to reset scroll state
    const debouncedScrollEnd = _debounce(() => {
      setIsUserScrolling(false);
    }, 150);

    const handleScroll = () => {
      setIsUserScrolling(true);
      debouncedScrollEnd();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Cancel any pending debounced calls
      debouncedScrollEnd.cancel();
    };
  }, []);

  const onAwsMapLoad = useCallback((e, containerRef) => {
    awsMapRef.current = e.target;
    if (!containerRef?.current) return;
    const debouncedResize = _debounce((e) => {
      e.target.resize();
    }, 200);
    mapContainerRef.current = containerRef.current;
    mapRef.current = e.target;
    const observer = new ResizeObserver(() => {
      e.target.easeTo({
        duration: 1000
      });
      debouncedResize(e);
    });
    observer.observe(containerRef.current);
    resizeObserverRef.current = observer;
  }, []);

  // Use the utility functions to transform locations based on the map provider
  const mapProvider = config?.maps_config?.location_service_provider;
  const useAwsMap = mapProvider === MAP_PROVIDERS.AWS;

  const locations = React.useMemo(() => {
    if (useAwsMap) {
      return transformLocationsForAwsMap(providers, isAdvancedMarkersEnabled);
    }
    return transformLocationsForGoogleMap(providers, isAdvancedMarkersEnabled);
  }, [providers, useAwsMap, isAdvancedMarkersEnabled]);

  /**
   * Find the complete location object by ID from providers' location data
   * @param {string} locationId - ID of the location to find
   * @returns {Object|null} - Complete location object or null if not found
   */
  const findLocationById = useCallback(
    (locationId) => {
      if (!providers || !locationId) return null;

      for (const provider of providers) {
        if (provider.locations) {
          const location = provider.locations.find(
            (loc) => loc.id === locationId
          );
          if (location) return location;
        }
      }

      return null;
    },
    [providers]
  );

  const handleMarkerClick = useCallback(
    (e) => {
      const {
        marker,
        meta: { location }
      } = e;

      // Store map reference for later manipulation if needed
      if (marker && marker.map) {
        mapRef.current = marker.map;
      }

      // Get location ID from marker metadata
      const locationId = location.id;

      // Find the complete location object
      const fullLocation = findLocationById(locationId) || location;

      if (log) {
        log('user_action.search_page.map.location_pin_click', {
          locationId,
          locationName: fullLocation.name || 'Unknown'
        });
      }

      if (isMapProviderCardEnabled) {
        const mapProvider = useAwsMap
          ? MAP_PROVIDERS.AWS
          : MAP_PROVIDERS.GOOGLE;
        setSelectedLocationId(locationId);
        setShowProviderCard(true);
        calculateCenterRelativeToMarker(mapProvider, marker, 330);
      } else {
        onMarkerClick({
          location: fullLocation,
          locationId
        });
      }
    },
    [
      log,
      onMarkerClick,
      findLocationById,
      setSelectedLocationId,
      calculateCenterRelativeToMarker,
      isMapProviderCardEnabled,
      MAP_PROVIDERS
    ]
  );

  const onBookAppointment = useCallback(() => {
    if (log) {
      log(
        'user_action.search_page.map.provider_card.book_appointment_button_click'
      );
    }
  }, [log]);

  const handleSearchAreaClick = useCallback(() => {
    if (showProviderCard) {
      setShowProviderCard(false);
    }
    if (log) {
      log('user_action.search_page.map.search_this_area_button_click');
    }
    setShowSearchAreaButtonInMap(false);
  }, [showProviderCard]);

  const resetSelectedMarkerIcons = useCallback(
    (locationId) => {
      const markers = markersRef.current;
      if (!markers?.length) return;

      const matchingMarkers = markers.filter(
        (markerData) => markerData?.meta?.location?.id === locationId
      );

      matchingMarkers.forEach((markerData) => {
        if (useAwsMap) {
          resetAwsMarkerIcon(markerData);
        } else {
          resetMarkersState({
            marker: matchingMarkers[0].marker,
            meta: matchingMarkers[0].meta,
            enabledLocationIds: locationIdsSet,
            selectedLocation: { id: locationId },
            gMaps: window.google.maps,
            theme: theme,
            enableAdvancedMarkers: isAdvancedMarkersEnabled
          });
        }
      });
    },
    [useAwsMap, locationIdsSet, isAdvancedMarkersEnabled]
  );

  const resetAwsMarkerIcon = useCallback((markerData) => {
    const { marker, meta } = markerData;
    meta.isFocused = false;
    meta.isClicked = false;
    const markerElement = marker?.getElement?.();
    if (markerElement) {
      markerElement.src = getMarkerIconForAws(AwsMarkerState.DEFAULT, {
        count: markerData?.meta?.location?.providerCount || 1
      });
      markerElement.style.zIndex = ZINDEXES.DEFAULT;
    }
  }, []);

  const onViewProvider = useCallback(() => {
    log('user_action.search_page.map.provider_card.view_profile_click');
  }, [log]);

  const hideMapProviderCard = useCallback(() => {
    if (!isMapProviderCardEnabled) {
      return;
    }

    setSelectedLocationId((prevLocationId) => {
      if (!prevLocationId) {
        return null;
      }
      resetSelectedMarkerIcons(prevLocationId);

      markersRef.current.forEach((markerData) => {
        if (markerData?.meta?.location?.id === prevLocationId) {
          markerData.meta.isFocused = false;
          markerData.meta.isClicked = false;
          if (mapProvider === MAP_PROVIDERS.AWS) {
            markerData.marker.getElement().focus();
          } else if (
            markerData.marker &&
            typeof markerData.marker.getUnderlyingMarker === 'function'
          ) {
            markerData.marker.getUnderlyingMarker().focus();
          }
        }
      });
      setShowProviderCard(false);
      return null;
    });
    log('user_action.search_page.map.provider_card.close');
  }, [isMapProviderCardEnabled, resetSelectedMarkerIcons, log]);

  useEffect(() => {
    if (markersRef?.current?.length > 0) {
      hideMapProviderCard();
    }
  }, [location]);

  const searchAreaEnabled = _get(
    config,
    'darkship_feature_flags.map_show_search_this_area_button',
    false
  );

  const shouldShowSearchAreaButton = useCallback(() => {
    return (
      isUserScrolling === false &&
      isMapInitialized &&
      searchAreaEnabled &&
      !showProviderCard
    );
  }, [isUserScrolling, isMapInitialized, searchAreaEnabled, showProviderCard]);

  const fitMapToBounds = useCallback(
    (mapInstanceRef) => {
      if (!mapInstanceRef.current) return;
      const bounds = new window.google.maps.LatLngBounds();

      // Add each location to the bounds
      mapLocations.forEach((location) => {
        bounds.extend({
          lat: location.coordinates.lat,
          lng: location.coordinates.lon
        });
      });

      // Store bounds for future reference
      mapBoundsRef.current = bounds;

      // Fit the map to the bounds with padding
      mapInstanceRef.current.fitBounds(bounds, {
        // Add padding to ensure markers aren't right at the edge
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
    },
    [mapLocations]
  );

  // Process provider data when it changes (including pagination)
  useEffect(() => {
    if (!providers || !config) {
      return;
    }

    // Update state with transformed locations
    setMapLocations(locations);
    setLocationIdsSet(new Set(locations.map((loc) => loc.id)));
  }, [providers, config, locations]);

  // If there are no providers or no config, return null
  if (!config) {
    return null;
  }

  // Check if AWS credentials are available when AWS Map is selected
  const hasAwsCredentials = !!(
    appSettings?.AWS_MAPS_CREDENTIALS?.mapName &&
    appSettings?.AWS_MAPS_CREDENTIALS?.mapRegion &&
    appSettings?.AWS_MAPS_CREDENTIALS?.mapIdentityPoolId
  );

  // If AWS Map is selected but credentials are missing, fall back to Google Maps
  if (useAwsMap && !hasAwsCredentials) {
    // Add warning in development mode to help with debugging configuration issues
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        'AWS Map selected but credentials are missing. Falling back to Google Maps.'
      );
    }
    // Option 1: Fall back to Google Maps (what we're doing)
    // Option 2: Return null to prevent rendering with incomplete configuration
    // return null;
  }

  const getMarkers = (markers) => {
    markersRef.current = markers;
    setMarkers(markers);
  };

  if (useAwsMap && hasAwsCredentials) {
    const mapOptions = {};
    if (mapZoom) {
      mapOptions.zoom = mapZoom;
    }
    if (mapCenter) {
      mapOptions.center = mapCenter;
    }

    return (
      <ResizableMapSection
        data-testid="provider-aws-map-container"
        searchAreaEnabled={showSearchAreaButtonInMap}
        onSearchAreaClick={handleSearchAreaClick}
        getUpdatedSearch={getUpdatedSearch}
        mapRef={awsMapRef}
        mapProvider={MAP_PROVIDERS.AWS}
      >
        {isMapProviderCardEnabled && showProviderCard && (
          <ProviderOverlay
            mapContainer={mapContainerRef}
            providers={providers}
            locationId={selectedLocationId}
            location={location}
            match={match}
            config={config}
            onEscape={hideMapProviderCard}
            onBookAppointment={onBookAppointment}
            onViewProvider={onViewProvider}
          />
        )}
        <AwsMap
          awsMapConfig={{
            identityPoolId: appSettings.AWS_MAPS_CREDENTIALS.mapIdentityPoolId,
            mapName: appSettings.AWS_MAPS_CREDENTIALS.mapName,
            region: appSettings.AWS_MAPS_CREDENTIALS.mapRegion
          }}
          mapOptions={mapOptions}
          locations={locations}
          getMarkers={getMarkers}
          onMarkerClick={handleMarkerClick}
          // Register map events for AWS Map if needed
          mapEventsToRegister={{
            load: onAwsMapLoad,
            dragstart: () => {
              if (searchAreaEnabled) {
                setShowSearchAreaButtonInMap(true);
              }
            },
            zoom: () => {
              if (searchAreaEnabled) {
                setShowSearchAreaButtonInMap(true);
              }
            },
            click: () => {
              hideMapProviderCard();
            }
          }}
          showFullscreenControl={false}
          showMultipleMarkersForSameLocation={true}
          enableAdvancedMarkers={isAdvancedMarkersEnabled}
          defaultMarkerIcon={getMarkerIconForAws(AwsMarkerState.DEFAULT)}
        />
      </ResizableMapSection>
    );
  }

  // Default to Google Maps if AWS Map is not selected or credentials are missing
  return (
    <ResizableMapSection
      data-testid="provider-google-map-container"
      searchAreaEnabled={showSearchAreaButtonInMap}
      onSearchAreaClick={handleSearchAreaClick}
      getUpdatedSearch={getUpdatedSearch}
      mapRef={mapRef}
      mapProvider={MAP_PROVIDERS.GOOGLE}
    >
      <>
        {isMapProviderCardEnabled && showProviderCard && (
          <ProviderOverlay
            mapContainer={mapContainerRef}
            providers={providers}
            locationId={selectedLocationId}
            location={location}
            match={match}
            config={config}
            onEscape={hideMapProviderCard}
            onBookAppointment={onBookAppointment}
            onViewProvider={onViewProvider}
          />
        )}
        <DynamicGmap
          locations={mapLocations}
          enabledLocationIds={locationIdsSet}
          mapOptions={{
            maxZoom: 16,
            zoom: mapZoom,
            fullscreenControl: false,
            mapTypeControl: false,
            zoomControl: true,
            streetViewControl: true,
            center: mapCenter,
            styles: [
              // Simplify points of interest
              { featureType: 'poi', stylers: [{ visibility: 'off' }] },
              // Simplify road labels
              {
                featureType: 'road',
                elementType: 'labels.icon',
                stylers: [{ visibility: 'off' }]
              },
              // Keep transit visible but simplified
              {
                featureType: 'transit',
                stylers: [{ visibility: 'simplified' }]
              }
            ],
            restriction: {
              // map restrictions
              latLngBounds: {
                north: NORTH_LATITUDE_BOUND,
                south: SOUTH_LATITUDE_BOUND,
                west: WEST_LONGITUDE_BOUND,
                east: EAST_LONGITUDE_BOUND
              },
              strictBounds: true
            }
          }}
          shouldLabelMarkers={false}
          onMarkerClick={handleMarkerClick}
          theme={config.theme}
          getMarkers={(markers) => {
            markersRef.current = markers;
            setMarkers(markers);
            if (mapRef.current && markersRef.current.length > 0) {
              fitMapToBounds(mapRef);
            }
          }}
          mapEventsToRegister={{
            // Fit bounds when map is fully loaded
            idle: (mapInstanceRef) => {
              if (mapInstanceRef.current && !mapRef.current) {
                mapRef.current = mapInstanceRef.current;

                mapContainerRef.current =
                  mapInstanceRef.current.getDiv().parentElement;
                if (window.google && window.google.maps) {
                  mapInitTimeoutRef.current = setTimeout(() => {
                    setIsMapInitialized(true);
                  }, 100);
                }
              }
            },
            zoom_changed: () => {
              if (shouldShowSearchAreaButton()) {
                setShowSearchAreaButtonInMap(true);
              }
            },
            center_changed: () => {
              if (shouldShowSearchAreaButton()) {
                setShowSearchAreaButtonInMap(true);
              }
            },
            click: () => {
              hideMapProviderCard();
            }
          }}
          enableAdvancedMarkers={isAdvancedMarkersEnabled}
          defaultMarkerIcon={getMarkerIconForGmaps(GmapMarkerStates.DEFAULT)}
        />
      </>
    </ResizableMapSection>
  );
}

ProviderMapContainer.propTypes = {
  providers: PropTypes.array,
  config: PropTypes.object,
  appSettings: PropTypes.object,
  hoveredProviderId: PropTypes.string,
  log: PropTypes.func,
  onMarkerClick: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object
};

ProviderMapContainer.defaultProps = {
  providers: [],
  config: null,
  appSettings: null,
  hoveredProviderId: null,
  log: () => {},
  onMarkerClick: () => {}
};

export default ProviderMapContainer;
